import type {
  ComponentPropsWithRef,
  ComponentPropsWithoutRef,
  ElementType,
  ReactElement,
  ReactNode,
} from 'react';
import { forwardRef } from 'react';

type AsProp<Comp extends ElementType> = {
  as?: Comp;
  // `ComponentPropsWithRef<Comp>['ref']` resolves the correct ref type for host
  // tags, class components AND function components. The previous hand-rolled
  // `ElementTagNameMap`/`InstanceType` ternary fell through to `Ref<undefined>`
  // for `as={FunctionComponent}`, silently leaving those refs untyped.
  ref?: ComponentPropsWithRef<Comp>['ref'];
} & Omit<ComponentPropsWithoutRef<Comp>, 'as'>;

type CompWithAsProp<Props, DefaultElementType extends ElementType> = {
  <Comp extends ElementType = DefaultElementType>(
    props: AsProp<Comp> & Props
  ): ReactElement;
  displayName?: string;
};

/**
 * Polymorphic `forwardRef` helper for the design system's `as`-able primitives.
 *
 * IMPORTANT — keep `React.forwardRef`; do NOT migrate this to the React 19
 * "ref as a prop" model. The design system is consumed by the reckon-one
 * classic-bridge, which externalizes React and runs against the host page's
 * React 16. In React < 19 a function component never receives `ref` as a prop,
 * so dropping `forwardRef` here would silently break ref forwarding for every
 * DS primitive in that host. The react-doctor `no-react19-deprecated-apis`
 * warning on `forwardRef` is therefore not actionable for this file.
 */
export const forwardRefWithAs = <
  DefaultElementType extends ElementType,
  BaseProps
>(
  render: (
    props: BaseProps & { as?: ElementType },
    ref: React.Ref<any>
  ) => Exclude<ReactNode, undefined>
): CompWithAsProp<BaseProps, DefaultElementType> => {
  // `forwardRef`'s typed render signature doesn't accept this loose polymorphic
  // render, and its `ForwardRefExoticComponent` result is presented as the
  // generic-callable `CompWithAsProp`. The suppression is unrelated to the ref
  // typing above and is pre-existing.
  // @ts-ignore
  return forwardRef(render);
};
